import { call, put, takeLatest, select } from 'redux-saga/effects';

import {
  getIntegrations as getIntegrationsApi,
  getIntegrationTypes as getIntegrationTypesApi,
  createIntegration as createIntegrationApi,
  createIntegrationTemplate as createIntegrationTemplateApi,
  updateIntegration as updateIntegrationApi,
  updateIntegrationTemplate as updateIntegrationTemplateApi,
  deleteIntegration as deleteIntegrationApi,
  syncRemoteAssets as syncRemoteAssetsApi,
  createIntegratedSource,
  getIntegratedSources,
  updateIntegratedSource,
  importSource,
  createRemoteEvent as createRemoteEventApi,
} from '../../services';
import { cleanFormData } from '../../utils';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import { requestSources, getImportableSourceTypes } from '../sources';
import { requestComponents } from '../components';
import {
  REQUEST_INTEGRATIONS,
  CREATE_INTEGRATION,
  CREATE_INTEGRATION_TEMPLATE,
  UPDATE_INTEGRATION,
  UPDATE_INTEGRATION_TEMPLATE,
  DELETE_INTEGRATION,
  SYNC_REMOTE_ASSETS,
  CREATE_REMOTE_EVENT,
  receiveIntegration,
  receiveIntegrations,
  removeIntegration,
} from './actions';
import { getSiteIntegrations } from './selectors';

export function* doRequestIntegrations(action) {
  const { query } = action;
  try {
    const { values: integrationTypes } = yield call(getIntegrationTypesApi);
    const { values } = yield call(getIntegrationsApi, query);
    yield put(receiveIntegrations(values, integrationTypes, query.site || query.org));
  } catch (e) {
    console.error('Unable to fetch integrations: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getIntegrations', 'error')()));
  }
}

export function* doSyncRemoteAssets(action) {
  const { integrationId, siteId, callback } = action;
  try {
    const { added, deleted, updated } = yield call(syncRemoteAssetsApi, integrationId);
    const message = `Synced assets: added (${added}), updated (${updated}), deleted (${deleted}).`;
    yield put(
      displayNotification(getNotification('syncAssets', 'success')(message, integrationId))
    );
    yield put(requestComponents(siteId));
    callback();
  } catch (e) {
    console.error('Unable to sync integrated assets: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('syncAssets', 'error')(integrationId)));
  }
}

export function* doCreateIntegration(action) {
  const { data: createIntegrationPayload } = action;
  try {
    const createdIntegration = yield call(createIntegrationApi, createIntegrationPayload);
    const message = [];
    if (createdIntegration) {
      message.push(`Created integration: ${createdIntegration.name}`);
      yield put(receiveIntegration(createdIntegration));

      const {
        id,
        site,
        org,
        type,
        name,
        configuration: { connection, data } = {},
      } = createdIntegration;
      const importableSourceTypes = yield select(getImportableSourceTypes);
      const isImportable = importableSourceTypes.includes(type);

      // check for brokered, asset-type integration
      if (createdIntegration.configuration?.asset && createdIntegration.type !== 'custom') {
        try {
          const { added, updated, deleted } = yield call(
            syncRemoteAssetsApi,
            createdIntegration.id,
            { startDate: 0 }
          );
          message.push(
            `Synced assets: added (${added}), updated (${updated}), deleted (${deleted}).`
          );
        } catch (e) {
          console.error('Unable to sync integrated assets: ', e);
          yield call(checkOnline);
          yield put(displayNotification(getNotification('syncAssets', 'error')(id)));
          message.push('*** Asset sync failed ***');
        }
      }

      // check for defined or importable data-type integration
      if (connection && (data || (type !== 'custom' && isImportable))) {
        try {
          const sourcePayload = cleanFormData({
            options: {
              ...connection,
              ...data,
            },
            name: `${name} source`,
            org,
            site,
            integration: id,
          });
          const source = yield call(createIntegratedSource, id, sourcePayload);
          message.push(`Created integrated source: ${source.name}`);

          const shouldImportSignals =
            (type !== 'custom' && isImportable) ||
            (data?.import?.enabled && data?.import?.endpoint);
          if (source && shouldImportSignals) {
            try {
              const { added, updated } = yield call(importSource, source.id);
              if (added || updated) {
                message.push(`Imported source signals: added (${added}), updated (${updated})`);
              }
            } catch (e) {
              console.error('Unable to import integrated source signals: ', e);
              yield call(checkOnline);
              yield put(
                displayNotification(getNotification('importSourceVars', 'error')(e.message))
              );
              message.push('*** Data source signal import failed ***');
            }
          }

          yield put(requestSources({ siteId: site }));
        } catch (e) {
          console.error('Unable to create integrated source: ', e);
          yield call(checkOnline);
          yield put(displayNotification(getNotification('createSource', 'error')(e.message)));
          message.push('*** Data source creation failed ***');
        }
      }
      yield put(displayNotification(getNotification('createIntegration', 'success')(message, id)));
    }
  } catch (e) {
    console.error('Encountered errors while creating integration: ', e);
    yield call(checkOnline);
    yield put(
      displayNotification(
        getNotification('createIntegration', 'error')(createIntegrationPayload.name)
      )
    );
  }
}

export function* doCreateIntegrationTemplate(action) {
  const { data } = action;
  try {
    const createdTemplate = yield call(createIntegrationTemplateApi, data);
    if (createdTemplate) {
      yield put(
        displayNotification(
          getNotification('createIntegration', 'success')(data.name, createdTemplate.id, true)
        )
      );
      yield put(receiveIntegration(createdTemplate));
    }
  } catch (e) {
    console.error('Unable to create integration: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('createIntegration', 'error')(data.name)));
  }
}

export function* doUpdateIntegration(action) {
  const { id, data: updateIntegrationPayload } = action;
  try {
    const updatedIntegration = yield call(updateIntegrationApi, id, updateIntegrationPayload);
    const message = [];
    if (updatedIntegration) {
      message.push(`Updated integration: ${updatedIntegration.name}`);
      yield put(receiveIntegration(updatedIntegration));

      const {
        site,
        org,
        type,
        name,
        configuration: { connection, data } = {},
      } = updatedIntegration;
      const importableSourceTypes = yield select(getImportableSourceTypes);
      const isImportable = importableSourceTypes.includes(type);

      // check for brokered, asset-type integration
      if (updatedIntegration.configuration?.asset && updatedIntegration.type !== 'custom') {
        try {
          const { added, updated, deleted } = yield call(
            syncRemoteAssetsApi,
            updatedIntegration.id
          );
          message.push(
            `Synced assets: added (${added}), updated (${updated}), deleted (${deleted}).`
          );
        } catch (e) {
          console.error('Unable to sync integrated assets: ', e);
          yield call(checkOnline);
          yield put(displayNotification(getNotification('syncAssets', 'error')(id)));
          message.push('*** Asset sync failed ***');
        }
      }

      // check for defined or importable data-type integration
      if (connection && (data || (type !== 'custom' && isImportable))) {
        try {
          const { values: [integratedSource] = [] } = yield call(getIntegratedSources, id, { org });
          const sourcePayload = cleanFormData({
            options: {
              ...connection,
              ...data,
            },
            name: `${name} source`,
            org,
            site,
            integration: id,
          });
          let updatedSource;
          if (integratedSource) {
            updatedSource = yield call(
              updateIntegratedSource,
              id,
              integratedSource.id,
              sourcePayload
            );
          } else {
            updatedSource = yield call(createIntegratedSource, id, sourcePayload);
          }
          message.push(`Updated integrated source: ${updatedSource.name}`);

          const shouldImportSignals =
            (type !== 'custom' && isImportable) ||
            (data?.import?.enabled && data?.import?.endpoint);
          if (updatedSource && shouldImportSignals) {
            try {
              const { added, updated } = yield call(importSource, updatedSource.id);
              if (added || updated) {
                message.push(`Imported source signals: added (${added}), updated (${updated})`);
              }
            } catch (e) {
              console.error('Unable to import integrated source signals: ', e);
              yield call(checkOnline);
              yield put(
                displayNotification(getNotification('importSourceVars', 'error')(e.message))
              );
              message.push('*** Data source signal import failed ***');
            }
          }

          yield put(requestSources({ siteId: site }));
        } catch (e) {
          console.error('Unable to create/update integrated source: ', e);
          yield call(checkOnline);
          yield put(
            displayNotification(getNotification('updateSource', 'error')(updatedIntegration.id))
          );
          message.push('*** Data source update failed ***');
        }
      }
      yield put(displayNotification(getNotification('updateIntegration', 'success')(message, id)));
    }
  } catch (e) {
    console.error('Encountered errors while updating integration: ', e);
    yield call(checkOnline);
    yield put(
      displayNotification(
        getNotification('updateIntegration', 'error')(updateIntegrationPayload.name, id)
      )
    );
  }
}

export function* doUpdateIntegrationTemplate(action) {
  const { id, data } = action;
  try {
    const updatedIntegration = yield call(updateIntegrationTemplateApi, id, data);
    if (updatedIntegration) {
      yield put(
        displayNotification(getNotification('updateIntegration', 'success')(data.name, id, true))
      );
      yield put(receiveIntegration(updatedIntegration));
    }
  } catch (e) {
    console.error('Unable to update integration: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateIntegration', 'error')(data.name, id)));
  }
}

export function* doDeleteIntegration(action) {
  const { id } = action;
  try {
    yield call(deleteIntegrationApi, id);
    yield put(removeIntegration(id));
    yield put(displayNotification(getNotification('deleteIntegration', 'success')(id)));
  } catch (e) {
    console.error('Unable to delete integration: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteIntegration', 'error')(id)));
  }
}

export function* doCreateRemoteEvent(action) {
  const { integrationType, data } = action;
  const integrations = yield select(getSiteIntegrations);
  const integration = integrations.find((i) => i.type === integrationType);
  if (integration) {
    try {
      const { entityUUID: remoteEventId } = yield call(createRemoteEventApi, integration.id, data);
      if (remoteEventId) {
        yield put(
          displayNotification(
            getNotification('createRemoteEvent', 'success')(data.name, remoteEventId)
          )
        );
      }
    } catch (e) {
      console.error('Unable to create remote event: ', e);
      yield call(checkOnline);
      yield put(displayNotification(getNotification('createRemoteEvent', 'error')(data.name)));
    }
  } else {
    yield put(displayNotification(getNotification('createRemoteEvent', 'error')(data.name)));
  }
}

export const sagas = [
  takeLatest(REQUEST_INTEGRATIONS, doRequestIntegrations),
  takeLatest(CREATE_INTEGRATION, doCreateIntegration),
  takeLatest(CREATE_INTEGRATION_TEMPLATE, doCreateIntegrationTemplate),
  takeLatest(UPDATE_INTEGRATION, doUpdateIntegration),
  takeLatest(UPDATE_INTEGRATION_TEMPLATE, doUpdateIntegrationTemplate),
  takeLatest(DELETE_INTEGRATION, doDeleteIntegration),
  takeLatest(SYNC_REMOTE_ASSETS, doSyncRemoteAssets),
  takeLatest(CREATE_REMOTE_EVENT, doCreateRemoteEvent),
];
